"use client"

import { motion } from "framer-motion"
import { Brain, Zap, TrendingUp, Activity, Heart, Target, Clock, Award } from "lucide-react"

export default function StatsCards({ insights, activityCount }) {
  const stats = [
    {
      title: "Active Regions",
      value: insights?.brain_regions?.length || 0,
      icon: Brain,
      color: "#4361ee",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      description: "Brain regions currently active",
    },
    {
      title: "Cognitive Effects",
      value: insights?.cognitive_effects?.length || 0,
      icon: Zap,
      color: "#f72585",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      description: "Active cognitive processes",
    },
    {
      title: "Activities Today",
      value: activityCount || 0,
      icon: Activity,
      color: "#4cc9f0",
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      description: "Total activities analyzed",
    },
    {
      title: "Stress Level",
      value: insights?.stress_level || "Normal",
      icon: Heart,
      color: "#06ffa5",
      gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      description: "Current stress indicator",
      isText: true,
    },
  ]

  const getStressColor = (level) => {
    if (!level) return "#06ffa5"
    const levelLower = level.toLowerCase()
    if (levelLower.includes("relax") || levelLower.includes("low")) return "#06ffa5"
    if (levelLower.includes("moderate") || levelLower.includes("normal")) return "#ffd60a"
    if (levelLower.includes("high") || levelLower.includes("elevat")) return "#ff006e"
    return "#06ffa5"
  }

  return (
    <div className="stats-cards-container">
      {stats.map((stat, index) => {
        const Icon = stat.icon
        const displayValue = stat.isText ? stat.value : stat.value
        const stressColor = stat.title === "Stress Level" ? getStressColor(stat.value) : stat.color

        return (
          <motion.div
            key={index}
            className="stat-card-modern glass-card"
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: index * 0.1, type: "spring", stiffness: 100 }}
            whileHover={{ 
              scale: 1.05, 
              y: -10,
              boxShadow: "0 20px 40px rgba(0,0,0,0.3)"
            }}
          >
            {/* Animated Background */}
            <div 
              className="stat-card-bg"
              style={{ background: stat.gradient }}
            />

            {/* Icon with Animation */}
            <motion.div 
              className="stat-card-icon"
              style={{ color: stressColor }}
              animate={{ 
                rotate: [0, 5, -5, 0],
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                repeatDelay: 3
              }}
            >
              <Icon size={32} />
            </motion.div>

            {/* Content */}
            <div className="stat-card-content">
              <h3 className="stat-card-title">{stat.title}</h3>
              <motion.div 
                className="stat-card-value"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: index * 0.1 + 0.2, type: "spring" }}
              >
                {stat.isText ? (
                  <span className="stat-text-value" style={{ color: stressColor }}>
                    {displayValue}
                  </span>
                ) : (
                  <span className="stat-number-value">{displayValue}</span>
                )}
              </motion.div>
              <p className="stat-card-description">{stat.description}</p>
            </div>

            {/* Decorative Elements */}
            <div className="stat-card-decoration">
              <motion.div
                className="decoration-circle"
                animate={{ 
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3]
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </div>

          </motion.div>
        )
      })}
    </div>
  )
}
